import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { approveRevocation, getRevocation, listRevocations, rejectRevocation, type RevocationQueueItem } from "@/api/client";
import { useSession } from "@/auth/session";
import { ApprovalTabs } from "@/components/ApprovalTabs";
import { DataTable, type Column } from "@/components/DataTable";
import { formatDate } from "@/components/format";
import { PagedFooter } from "@/components/Pagination";
import { pageParams } from "@/routes/paging";
import { EmptyState, ErrorBanner, StatusDot, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { useRevealPanel } from "@/components/useRevealPanel";

const revocationsApi = getRouteApi("/staff/approvals/revocations");

const columns: Column<RevocationQueueItem>[] = [
  {
    key: "certificate",
    header: "Certificate",
    primary: true,
    cell: (item) => (
      <>
        <span className="block font-medium text-zinc-950">{item.member_full_name}</span>
        <span className="block font-mono text-xs text-zinc-500">{item.doc_control_id}</span>
      </>
    ),
  },
  { key: "by", header: "Requested by", cell: (item) => <span className="text-zinc-700">{item.requested_by_name}</span> },
  { key: "at", header: "Requested", cell: (item) => <span className="text-zinc-500">{formatDate(item.created_at)}</span> },
];

export function RevocationQueue() {
  const { me, can } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = revocationsApi.useSearch();
  const { page, page_size } = search;
  const activeID = search.request ?? null;
  const [comment, setComment] = useState("");

  const requests = useQuery({
    queryKey: ["revocations", "pending", page, page_size],
    queryFn: () => listRevocations({ status: "pending", ...pageParams(search) }),
    refetchInterval: 15_000,
  });
  const items = requests.data?.items ?? [];
  const onPage = items.find((item) => item.id === activeID) ?? null;
  // A linked request may sit on another page, so look it up before calling it decided.
  const linked = useQuery({
    queryKey: ["revocation", activeID],
    queryFn: () => getRevocation(activeID!),
    enabled: activeID !== null && requests.isSuccess && onPage === null,
  });
  const active = onPage ?? (linked.data?.status === "pending" ? linked.data : null);
  // A linked request that has left the queue (decided by someone else).
  const stale = activeID !== null && active === null && requests.isSuccess && !linked.isPending;
  const mine = active !== null && active.requested_by === me?.user.id;

  const select = (request: string | undefined) => {
    // Clears carry-over state so a comment or error from the previous request never applies to this one.
    setComment("");
    approve.reset();
    reject.reset();
    void navigate({ to: "/approvals/revocations", search: { request, page, page_size } });
  };

  const settle = async () => {
    select(undefined);
    await queryClient.invalidateQueries({ queryKey: ["revocations"] });
    await queryClient.invalidateQueries({ queryKey: ["certificate"] });
    await queryClient.invalidateQueries({ queryKey: ["certificates"] });
  };
  const approve = useMutation({ mutationFn: approveRevocation, onSuccess: settle });
  const reject = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => rejectRevocation(id, note),
    onSuccess: settle,
  });
  const busy = approve.isPending || reject.isPending;
  const { panelRef, headingRef, reveal } = useRevealPanel(activeID);
  const pick = (id: string) => {
    reveal(id);
    select(id);
  };

  return (
    <section className="space-y-5">
      <ApprovalTabs />
      <WorkspaceHeader
        title="Revocation requests"
        detail={`${requests.data?.total ?? 0} request${requests.data?.total === 1 ? "" : "s"} awaiting a second approver`}
        action={<StatusDot label={requests.isFetching ? "Refreshing" : "Live"} />}
      />
      <ErrorBanner error={requests.error ?? approve.error ?? reject.error} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)] xl:gap-5">
        <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
          {items.length === 0 ? (
            <EmptyState title="No pending revocations" detail="Requests appear here until a second person decides them." />
          ) : (
            <div className="p-3 md:p-0">
              <DataTable
                rows={items}
                columns={columns}
                rowKey={(item) => item.id}
                label="Pending revocation requests"
                onSelect={(item) => pick(item.id)}
                selectedKey={activeID ?? undefined}
              />
            </div>
          )}
          <PagedFooter data={requests.data} page={page} pageSize={page_size} />
        </div>

        <aside ref={panelRef} className="h-fit rounded-lg border border-zinc-200 bg-white p-4">
          <h2 ref={headingRef} tabIndex={-1} className="text-sm font-semibold focus:outline-none">
            Decision detail
          </h2>
          {active ? (
            <div className="mt-4 space-y-4 text-sm">
              <div>
                {can("certificate.read") ? (
                  <Link
                    to="/certificates/$certificateId"
                    params={{ certificateId: active.certificate_id }}
                    className="font-mono text-xs font-medium underline"
                  >
                    {active.doc_control_id}
                  </Link>
                ) : (
                  <span className="font-mono text-xs font-medium">{active.doc_control_id}</span>
                )}
                <p className="mt-1 text-zinc-800">{active.member_full_name}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500">Reason given by {active.requested_by_name}</p>
                <p className="mt-1 whitespace-pre-wrap text-zinc-800">{active.reason}</p>
              </div>
              {mine ? (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-800">
                  You requested this revocation. Another approver must decide it.
                </p>
              ) : (
                <>
                  <label className="block">
                    <span className="font-medium text-zinc-700">Rejection comment</span>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={3}
                      maxLength={2000}
                      className="mt-1 w-full resize-none rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                    />
                  </label>
                  <p className="text-xs text-zinc-500">Approving revokes the certificate permanently.</p>
                  <div className="flex gap-2">
                    <ToolbarButton tone="danger" disabled={busy} onClick={() => approve.mutate(active.id)}>
                      Approve revocation
                    </ToolbarButton>
                    <ToolbarButton
                      disabled={busy || comment.trim() === ""}
                      onClick={() => reject.mutate({ id: active.id, note: comment.trim() })}
                    >
                      Reject
                    </ToolbarButton>
                  </div>
                </>
              )}
            </div>
          ) : stale ? (
            <div className="mt-4">
              <EmptyState
                title="No longer pending"
                detail="Someone else has already decided this request."
                compact
              />
            </div>
          ) : (
            <div className="mt-4">
              <EmptyState title="Select a request" detail="The reason and decision controls appear here." compact />
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
